import {useMemo} from 'react';
import {logger} from '@/helpers/logger';
import {patchSalesTarget} from '@/services/salesTargets';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {Constants} from '@/utils/constants/commonConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  buildDisplayContentUpdatePayload,
  getSalesTargetActiveWindowDates,
  isNowWithinDateWindow
} from '@/pages/SalesTargetUtils';

const normalizeStatus = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z]/g, '');

export const useDisplayContentPublish = ({
  salesTarget,
  salesTargetId,
  isEditDisplayContent,
  returnTo,
  persistDisplayContent,
  formData,
  dispatch,
  navigate
}) => {
  const {start, end} = getSalesTargetActiveWindowDates(salesTarget);
  const isInProgressStatus =
    normalizeStatus(salesTarget?.status) ===
    normalizeStatus(Constants.InProgress);
  const isPublishedStatus =
    normalizeStatus(salesTarget?.status) ===
    normalizeStatus(Constants.Published);
  const isPublishedBeforeStart = useMemo(() => {
    if (!isPublishedStatus || !start) return false;
    const startDate = new Date(start);
    if (Number.isNaN(startDate.getTime())) return false;
    return new Date() < startDate;
  }, [isPublishedStatus, start]);
  const canPatchDisplayContentNow =
    isEditDisplayContent &&
    (isInProgressStatus ||
      isNowWithinDateWindow(start, end) ||
      isPublishedBeforeStart);

  return async () => {
    try {
      persistDisplayContent();

      if (!canPatchDisplayContentNow) {
        navigate(returnTo);
        return;
      }

      const id = salesTargetId || salesTarget?.id;
      if (!id) {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Sales Target id is missing',
            type: 'error'
          })
        );
        return;
      }

      const response = await patchSalesTarget(
        id,
        buildDisplayContentUpdatePayload(formData)
      );
      if (response?.success === false) {
        dispatch(
          setToastNotifications({
            show: true,
            message: response?.message || 'Failed to update display content',
            type: 'error'
          })
        );
        return;
      }

      dispatch(
        setToastNotifications({
          show: true,
          message: 'Display Content updated',
          type: 'success'
        })
      );
      navigate(ROUTES.SALES_TARGETS);
    } catch (error) {
      logger.error('Error updating display content:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message:
            'An unknown error occurred while updating display content. Please try again.',
          type: 'error'
        })
      );
    }
  };
};
